import { BiddingBlockBidStatus } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import { BiddingSpotBidStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import type { TFunction } from 'i18next'

import type {
  BulkResultNoticeSpec,
  BulkToast,
} from '~/hooks/bidding/useBulkResultNotice'
import type { BulkOutcome } from '~/models/bulkResultNotice'
import type { BlockBidRow } from '~/pages/SpotBidding/models/blockBidRow'
import { resolveBlockBidGroupWithdrawState } from '~/pages/SpotBidding/models/blockBidWithdrawAction'
import type { BulkWithdrawSettled } from '~/pages/SpotBidding/models/bulkWithdrawResult'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'

/**
 * 一括取下 (AC-BSP-116) の結果トーストで追う 1 件。 通常入札は札、 ブロック入札はグループ
 * (LINK / LOOP の 2 Leg を 1 件と数える)。 key は通常入札が札 id、 ブロック入札がグループ id で、
 * 一括取下の応答の id と、 札ごとのトースト (AC-BSP-057b) の key と同じ。
 */
export type SpotBulkWithdrawNoticeItem =
  | { kind: 'normal'; key: string; bidStatus: BiddingSpotBidStatus }
  | {
      kind: 'block'
      key: string
      legBidStatuses: readonly BiddingBlockBidStatus[]
    }

/**
 * 取下失敗を観測した時点が入札締切の前か後か。 失敗のトーストの本文を選ぶ (AC-BSP-116)。
 * 失敗が確定した時刻は一覧に無いので、 1 件ずつの取下の添える文 (`withdrawStateHintKey`) と同じく
 * 観測した時点で決める。 今の取消の送信は入札締切を過ぎた時にだけ取下失敗にするので、 実際に出るのは
 * afterDeadline だけ (締切前に区切るかは PdM確認 #12411)。
 */
export type SpotBulkWithdrawFailure = 'beforeDeadline' | 'afterDeadline'

/**
 * screen の 1 回の観測 (通常入札の札とブロック入札の Leg 行) を、 一括取下の結果トーストで追う単位に
 * する。 ブロック入札は Leg 行をグループごとに 1 件にまとめ、 全 Leg の状態を持つ。
 */
export function spotBulkWithdrawNoticeItems(
  bids: ReadonlyArray<Pick<SpotBidRow, 'id' | 'bidStatus'>>,
  blockRows: ReadonlyArray<Pick<BlockBidRow, 'groupId' | 'bidStatus'>>,
): SpotBulkWithdrawNoticeItem[] {
  const groups = new Map<string, BiddingBlockBidStatus[]>()
  for (const row of blockRows) {
    const legs = groups.get(row.groupId)
    if (legs === undefined) groups.set(row.groupId, [row.bidStatus])
    else legs.push(row.bidStatus)
  }
  return [
    ...bids.map(
      (bid): SpotBulkWithdrawNoticeItem => ({
        kind: 'normal',
        key: bid.id,
        bidStatus: bid.bidStatus,
      }),
    ),
    ...[...groups].map(
      ([key, legBidStatuses]): SpotBulkWithdrawNoticeItem => ({
        kind: 'block',
        key,
        legBidStatuses,
      }),
    ),
  ]
}

/**
 * 一括で受け付けた札 (ブロック入札はグループ) の、 その観測での結果。
 * - 取消 (ブロックは全 Leg) → 成立
 * - 取下処理中 (ブロックは全 Leg) → 処理中
 * - 取下失敗 (ブロックは全 Leg) → 失敗。 理由は観測した時点が入札締切の前か後か
 * - それ以外 (取下処理中のまま約定した札・Leg が食い違うグループ 等) → 分からないので追い続ける
 */
export function classifySpotBulkWithdraw(
  item: SpotBulkWithdrawNoticeItem,
  isBeforeDeadline: boolean,
): BulkOutcome<SpotBulkWithdrawFailure> {
  const failed = {
    failed: isBeforeDeadline ? 'beforeDeadline' : 'afterDeadline',
  } as const
  if (item.kind === 'normal') {
    switch (item.bidStatus) {
      case BiddingSpotBidStatus.CANCELLED:
        return 'succeeded'
      case BiddingSpotBidStatus.WITHDRAWING:
        return 'pending'
      case BiddingSpotBidStatus.WITHDRAW_FAILED:
        return failed
      default:
        return 'unknown'
    }
  }
  const legs = item.legBidStatuses
  if (
    legs.length > 0 &&
    legs.every((s) => s === BiddingBlockBidStatus.CANCELLED)
  ) {
    return 'succeeded'
  }
  switch (resolveBlockBidGroupWithdrawState(legs)) {
    case 'withdrawing':
      return 'pending'
    case 'withdrawFailed':
      return failed
    default:
      return 'unknown'
  }
}

/** 通常入札の札の数とブロック入札のグループの数。 */
const countsOf = (
  items: readonly SpotBulkWithdrawNoticeItem[],
): { normal: number; block: number } => ({
  normal: items.filter((i) => i.kind === 'normal').length,
  block: items.filter((i) => i.kind === 'block').length,
})

/**
 * 失敗のトーストの本文。 すべて入札締切前に観測した失敗なら eSLJ での取消を案内する本文、 1 件でも
 * 入札締切以後の失敗があれば約定結果で確定する本文にする (件数はどちらも合計)。 入札締切後は取り消せ
 * ないため、 取消の手順を案内しない (AC-BSP-116 / AC-BSP-058)。
 */
export const spotBulkWithdrawFailureBodyKey = (
  reasons: readonly SpotBulkWithdrawFailure[],
): 'failedBodyBeforeDeadline' | 'failedBodyAfterDeadline' =>
  reasons.every((r) => r === 'beforeDeadline')
    ? 'failedBodyBeforeDeadline'
    : 'failedBodyAfterDeadline'

const KEY = 'page:biddingSpot.bulkWithdraw.notice'

/**
 * スポットの一括取下の結果トースト (一括操作の結果トーストに渡す市場ごとの部分。 AC-BSP-116)。
 * 成立・取下失敗が確定した時点で、 近い時刻の分を 1 通ずつ出す (perObservation。 全件を待たず、 再送の後に
 * 成立した札も知らせる)。
 * 件数は通常入札の札の数とブロック入札のグループの数で、 どちらかが 0 件でも「0件」 と出す
 * (確認ダイアログ・受け付けなかった札のバナーと同じ形)。 処理中のトーストは出さない。
 */
export function spotBulkWithdrawNoticeSpec(
  t: TFunction,
  isBeforeDeadline: boolean,
): BulkResultNoticeSpec<SpotBulkWithdrawNoticeItem, SpotBulkWithdrawFailure> {
  return {
    keyOf: (item) => item.key,
    classify: (item) => classifySpotBulkWithdraw(item, isBeforeDeadline),
    emit: 'perObservation',
    successToast: (items): BulkToast => ({
      type: 'success',
      title: t(`${KEY}.successTitle`),
      description: t(`${KEY}.successBody`, countsOf(items)),
    }),
    failureToast: (failures): BulkToast => ({
      type: 'error',
      title: t(`${KEY}.failedTitle`),
      description: t(
        `${KEY}.${spotBulkWithdrawFailureBodyKey(failures.map((f) => f.reason))}`,
        countsOf(failures.map((f) => f.item)),
      ),
    }),
  }
}

/**
 * 一括取下の 2 本の RPC の応答で、 取下を受け付けた札 (通常入札は札 id、 ブロック入札はグループ id)。
 * 受け付けなかった札と、 RPC が失敗して受け付けたか分からない札は含めない。
 */
export function bulkWithdrawAcceptedKeys(
  spot: BulkWithdrawSettled,
  block: BulkWithdrawSettled,
): string[] {
  return [spot, block].flatMap((settled) =>
    settled.status === 'fulfilled'
      ? settled.value.results
          .filter((r) => r.outcome.case === 'accepted')
          .map((r) => r.id)
      : [],
  )
}
